import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Swords, Users, Lock, Target, Settings } from "lucide-react";
import StudyMateLogo from "../components/StudyMateLogo";
import Chalkboard from "../components/Chalkboard";
import SettingsModal from "../components/SettingsModal";
import LightRays from "../components/LightRays";
import { ParticleCard, useMobileDetection } from "../components/MagicBento";

// Purple-blue (indigo) glow for the MagicBento hover effects
const BENTO_GLOW_COLOR = "99, 102, 241";

const MODES = [
  { key: "casual",      title: "CASUAL",      subtitle: "1v1 — Find a random opponent",          icon: Swords, path: "/casual",      color: "#3B82F6" },
  { key: "multiplayer", title: "MULTIPLAYER", subtitle: "Team up with friends and take on others", icon: Users,  path: "/multiplayer", color: "#A855F7" },
  { key: "private",     title: "PRIVATE",     subtitle: "Invite friends and play custom matches",  icon: Lock,   path: "/private",     color: "#14B8A6" },
  { key: "practice",    title: "PRACTICE",    subtitle: "Practice against AI and improve",         icon: Target, path: "/practice",    color: "#F59E0B" },
];

export default function Home() {
  const nav = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const modeGridRef = useRef(null);
  const isMobile = useMobileDetection();

  // Drive the chalkboard hover-reveal mask (--mx/--my inherit down to .sm-chalkboard)
  const handleRevealMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };
  const handleRevealLeave = (e) => {
    e.currentTarget.style.setProperty("--mx", "-9999px");
    e.currentTarget.style.setProperty("--my", "-9999px");
  };

  return (
    <div
      className="relative min-h-0 sm:min-h-[calc(100vh-64px)] overflow-hidden home-hero-page"
      onMouseMove={handleRevealMove}
      onMouseLeave={handleRevealLeave}
    >
      {/* Interactive WebGL spotlight (replaces the old CSS .sm-spotlight) */}
      <div className="absolute inset-0 z-0 pointer-events-none home-light-rays" aria-hidden="true">
        <LightRays
          raysOrigin="top-center"
          raysColor="#7EB0FF"
          raysSpeed={1.2}
          lightSpread={0.9}
          rayLength={1.4}
          followMouse={true}
          mouseInfluence={0.15}
          noiseAmount={0.08}
          distortion={0.04}
        />
      </div>

      <Chalkboard />

      {/* Center stack: Logo + 4 mode buttons */}
      <div className="relative z-[1] max-w-xl mx-auto px-4 pt-8 sm:pt-16 pb-16 sm:pb-24 flex flex-col items-center">
        <StudyMateLogo />

        {/* Cursor spotlight + glow vars now come from the app-wide
            GlobalHoverEffects layer mounted in App.js */}
        <div
          ref={modeGridRef}
          className="mt-5 sm:mt-12 w-full home-mode-list bento-section flex flex-col gap-3.5 sm:gap-4"
        >
          {MODES.map((m, i) => (
            <ParticleCard
              key={m.key}
              as={motion.button}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + i * 0.08, duration: 0.5 }}
              onClick={() => nav(m.path)}
              className="sm-mode-btn magic-bento-card magic-bento-card--border-glow"
              style={{ "--mode-color": m.color, "--glow-color": BENTO_GLOW_COLOR }}
              disableAnimations={isMobile}
              particleCount={8}
              glowColor={BENTO_GLOW_COLOR}
              enableTilt={true}
              clickEffect={true}
              enableMagnetism={true}
              data-testid={`mode-${m.key}`}
            >
              <div className="sm-mode-icon">
                <m.icon className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.1} />
              </div>
              <div className="text-left flex-1 min-w-0">
                <div className="font-heading font-black text-lg sm:text-2xl tracking-wide">{m.title}</div>
                <div className="text-soft text-xs sm:text-sm mt-0.5">{m.subtitle}</div>
              </div>
            </ParticleCard>
          ))}
        </div>
      </div>

      {/* Bottom-left utility: Settings (help icon removed for a cleaner menu) */}
      <div className="absolute bottom-5 left-4 sm:left-8 z-10 flex items-center gap-2">
        <button
          onClick={() => setSettingsOpen(true)}
          className="sm-card p-3 hover:border-blue-500/50 transition-colors"
          data-testid="home-settings"
          aria-label="Settings"
        >
          <Settings size={18} className="text-soft" />
        </button>
      </div>

      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}
